import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

export interface FilterOption {
  id: string;
  label: string;
  color?: string;
}

interface Props {
  label: string;
  options: FilterOption[];
  selected: string | null;
  onChange: (id: string | null) => void;
  /** Texto que aparece cuando no hay filtro activo. Default: "Todos". */
  allLabel?: string;
}

export default function FilterDropdown({
  label,
  options,
  selected,
  onChange,
  allLabel = 'Todos',
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const selectedOption = selected ? options.find((o) => o.id === selected) : null;
  const showSearch = options.length > 6;
  const filtered = showSearch
    ? options.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase()))
    : options;

  const buttonLabel = selectedOption?.label ?? allLabel;
  const buttonColor = selectedOption?.color ?? '#475569';

  return (
    <div ref={wrapRef} style={styles.wrap}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          ...styles.btn,
          borderColor: selected ? buttonColor : '#e2e8f0',
        }}
      >
        <span style={styles.btnLabel}>{label}:</span>
        <span style={{ ...styles.btnValue, color: buttonColor, fontWeight: selected ? 700 : 500 }}>
          {buttonLabel}
        </span>
        <span style={styles.caret}>▾</span>
      </button>

      {open && (
        <div style={styles.popover}>
          {showSearch && (
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar…"
              style={styles.search}
            />
          )}
          <button
            type="button"
            onClick={() => { onChange(null); setOpen(false); setQuery(''); }}
            style={{ ...styles.item, ...(selected === null ? styles.itemActive : null) }}
          >
            <span style={{ ...styles.dot, background: '#cbd5e1' }} />
            <span style={{ flex: 1 }}>{allLabel}</span>
          </button>
          {filtered.length === 0 ? (
            <div style={styles.empty}>Sin coincidencias.</div>
          ) : (
            filtered.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => { onChange(o.id); setOpen(false); setQuery(''); }}
                style={{ ...styles.item, ...(selected === o.id ? styles.itemActive : null) }}
              >
                <span style={{ ...styles.dot, background: o.color ?? '#94a3b8' }} />
                <span style={{ flex: 1, color: o.color ?? '#0f172a' }}>{o.label}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  wrap: { position: 'relative' },
  btn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: '#fff',
    border: '1px solid',
    borderRadius: 999,
    padding: '6px 12px',
    fontSize: 13,
    cursor: 'pointer',
  },
  btnLabel: { color: '#94a3b8' },
  btnValue: {},
  caret: { color: '#94a3b8', fontSize: 10, marginLeft: 2 },
  popover: {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    right: 0,
    minWidth: 220,
    maxHeight: 320,
    overflowY: 'auto',
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    boxShadow: '0 8px 20px rgba(15,23,42,0.1)',
    zIndex: 80,
    padding: 4,
  },
  search: {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    padding: '6px 10px',
    fontSize: 13,
    outline: 'none',
    marginBottom: 4,
  },
  item: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 10px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 13,
    color: '#0f172a',
    textAlign: 'left',
    borderRadius: 6,
  },
  itemActive: {
    background: '#f1f5f9',
    fontWeight: 600,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  empty: {
    padding: '10px 12px',
    color: '#94a3b8',
    fontSize: 12,
  },
};
